/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  SessionErrorResponse,
  SessionErrorResponseCodeEnum
} from "client-code/session-manager";
import type { Session as DomainSession } from "@tivocorp/data-domain-lib";

import type { Session } from "../../entities";
import { SessionInteractor } from "../../interactor";
import { PlaybackContentType, PlaybackErrorType, SessionState } from "../enums";
import { PlaybackError } from "../error";
import type {
  IPlaybackError,
  ISessionCreateModel,
  IStreamingSession
} from "../interfaces";
import { Log, Diagnostics } from "../../logging";
import { SessionErrorMapper } from "../error";

/**
 * Abstract base class for all streaming session implementations.
 * Holds shared state (session, session state, error) and provides
 * common getter/setter implementations. Each content type subclass
 * implements createStreamingSession with its own backend call.
 */
export abstract class StreamingSessionImpl implements IStreamingSession {
  protected readonly TAG: string = "StreamingSessionImpl";

  private static readonly MSG_SESSION_STOP: string = "sessionStop()";

  protected mSessionState: SessionState = SessionState.NONE;

  protected mSession: Session = {
    playlistUrl: "",
    trickModeRestrictions: {},
    keepAliveIntervalSeconds: 0
  };

  protected mDomainSession: DomainSession | null = null;

  protected mError: IPlaybackError = { type: PlaybackErrorType.GENERAL_ERROR };

  public abstract createStreamingSession(
    sessionCreateModel: ISessionCreateModel
  ): Promise<void>;

  /**
   * Picks the playlist URL for a newly created session: the session's own
   * `playlistUrl` when the backend supplied one, otherwise the `playUri` the
   * caller asked to play, otherwise an empty string.
   *
   * Shared by all four content-type subclasses, which previously each carried
   * an identical copy.
   *
   * Note this deliberately tests for `undefined` rather than falsiness: an
   * empty-string `playlistUrl` is a value the backend actually sent and must
   * win over `playUri`, which `||` chaining would not honour. Optional
   * chaining and `??` are banned here — see docs/CHROMIUM-52-COMPATIBILITY.md.
   *
   * @param playlistUrl - `playlistUrl` from the created domain session.
   * @param playUri - `playUri` from the session-create model.
   * @returns The URL to play; `""` when neither source supplied one.
   */
  protected static resolvePlaylistUrl(
    playlistUrl: string | undefined,
    playUri: string | undefined
  ): string {
    if (playlistUrl !== undefined) {
      return playlistUrl;
    }
    if (playUri !== undefined) {
      return playUri;
    }
    return "";
  }

  protected static resolveLocalityId(session: object): string | undefined {
    if (!("localityId" in session)) {
      return undefined;
    }
    return typeof session.localityId === "string"
      ? session.localityId
      : undefined;
  }

  protected log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](this.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      this.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(this.TAG, "stacktrace:,", optionalParams[0]);
    }
  }

  public sessionKeepAlive(): Promise<void> {
    const { sessionId } = this.mDomainSession || {};
    if (!sessionId) {
      return Promise.resolve();
    }
    return new SessionInteractor()
      .sessionKeepAlive(this.mDomainSession!)
      .then(() => {
        this.log("debug", "sessionKeepAlive(): success");
      })
      .catch((error: unknown) => {
        this.log("error", "sessionKeepAlive()", error);
      });
  }

  public sessionStop(): void {
    this.log("info", StreamingSessionImpl.MSG_SESSION_STOP);
    const { sessionId } = this.mDomainSession || {};
    if (sessionId) {
      new SessionInteractor()
        .sessionDelete(this.mDomainSession!)
        .then(() => {
          this.log("debug", "sessionStop(): session deleted");
        })
        .catch((error: unknown) => {
          this.log("error", StreamingSessionImpl.MSG_SESSION_STOP, error);
        });
    }
    this.mDomainSession = null;
    this.mSession = {
      playlistUrl: "",
      trickModeRestrictions: {},
      keepAliveIntervalSeconds: 0
    };
    this.mSessionState = SessionState.NONE;
  }

  public getPlaybackError(
    errorCode: string,
    errorMessage?: string,
    errorTitle?: string,
    errorDescription?: string
  ): IPlaybackError {
    return {
      type: PlaybackErrorType.SESSION_ERROR,
      data: new PlaybackError(
        errorCode,
        errorMessage,
        errorTitle,
        errorDescription
      )
    };
  }

  /**
   * Builds a session {@link IPlaybackError} from a caught session-create
   * failure by mapping it onto the normalized session error model — the Unity
   * code plus its localized title and message. Recording playbacks pass
   * `isNdvrPlayback` so the nDVR-specific codes/messages are used.
   */
  protected getSessionError = (
    error: unknown,
    playbackContentType: PlaybackContentType
  ) => {
    const response =
      error && typeof error === "object"
        ? (error as { response?: { data?: unknown } }).response
        : undefined;
    const sessionError =
      response && response.data
        ? new SessionErrorMapper().getErrorByErrorObject(
            response.data as SessionErrorResponse,
            playbackContentType
          )
        : new SessionErrorMapper().getErrorByCode(
            SessionErrorResponseCodeEnum.InternalError
          );

    Diagnostics.info(
      this.TAG,
      `handleSessionError`,
      JSON.stringify(sessionError)
    );
    Log.i(this.TAG, `handleSessionError`, JSON.stringify(sessionError));
    return this.getPlaybackError(
      sessionError.getCode(),
      sessionError.getMessage(),
      sessionError.getTitle(),
      sessionError.getDescription()
    );
  };

  public getSessionState(): SessionState {
    return this.mSessionState;
  }

  public setSessionState(state: SessionState): void {
    this.mSessionState = state;
  }

  public getSessionPlaybackError(): IPlaybackError {
    return this.mError;
  }

  public setSessionPlaybackError(error: IPlaybackError): void {
    this.mError = error;
  }

  public getSessionInfo(): Session {
    return this.mSession;
  }
}
